import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { httpsCallable } from '@/lib/callableProxy';
import { functions as firebaseFunctions } from '@/config/firebase'; 
import { toast as sonnerToast } from "sonner";
import { useNavigate } from 'react-router-dom';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAppStore } from '@/stores/appStore';
import { Loader2, Trash2, Sun, Moon, LogOut } from 'lucide-react';
import { ReauthDialog } from '@/components/settings/account/ReauthDialog';

const AccountSettings = () => {
    const { currentUser, logout, sendPasswordReset, isAnonymous } = useAuth();
    const { theme, setTheme } = useAppStore();
    const navigate = useNavigate();

    const [isDeletingAccount, setIsDeletingAccount] = useState(false);
    const [showReauthDialog, setShowReauthDialog] = useState(false);

    const initiateAccountDeletion = () => {
        const providerId = currentUser?.providerData[0]?.providerId;
        if (providerId === 'password') {
            setShowReauthDialog(true);
        } else {
            performDeleteAccount();
        }
    };

    const handleChangePassword = async () => {
        if (!currentUser?.email) return;
        const toastId = sonnerToast.loading("Sending email...");
        try {
            await sendPasswordReset(currentUser.email);
            sonnerToast.success("Check your inbox for the reset link.", { id: toastId });
        } catch (error: any) {
            sonnerToast.error("Failed to send email.", { id: toastId });
        }
    };

    const performDeleteAccount = async () => {
        setIsDeletingAccount(true);
        const toastId = sonnerToast.loading("Deleting account...");
        try {
            const deleteUserFn = httpsCallable(firebaseFunctions, 'deleteUserAccount');
            await deleteUserFn();
            sonnerToast.success("Account deleted.", { id: toastId });
            await logout();
            navigate('/auth', { replace: true });
        } catch (error: any) {
            sonnerToast.error("Delete failed.", { id: toastId });
        } finally {
            setIsDeletingAccount(false);
        }
    };

    return (
        <div className="space-y-8">
            {/* Seller Profile */}
            <Card className="rounded-3xl border-slate-200 bg-white">
                <CardHeader className="p-8 pb-4">
                    <CardTitle className="text-2xl tracking-[-0.03em] text-slate-950">Seller Profile</CardTitle>
                </CardHeader>
                <CardContent className="p-8 pt-2 space-y-6">
                    {isAnonymous ? (
                        <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 text-center">
                            <p className="mb-2 text-slate-500">You are using a temporary guest account.</p>
                            <Button onClick={() => navigate('/auth?mode=signup')} variant="default" className="rounded-full bg-slate-900 text-white hover:bg-slate-800">Sign Up to Save Data</Button>
                        </div>
                    ) : (
                        <>
                            <div className="grid gap-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Email Address</Label>
                                <div className="text-lg font-medium text-slate-900">{currentUser?.email}</div>
                            </div>
                            <div className="flex gap-4">
                                <Button variant="outline" onClick={handleChangePassword} className="rounded-xl border-slate-200 bg-white hover:bg-slate-50">
                                    Reset Password
                                </Button>
                                <Button variant="ghost" onClick={() => logout()} className="text-slate-500 hover:bg-slate-50 hover:text-slate-900">
                                    <LogOut size={16} className="mr-2"/> Sign Out
                                </Button>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Appearance */}
            <Card className="rounded-3xl border-slate-200 bg-white">
                 <CardHeader className="p-8 pb-4">
                    <CardTitle className="text-2xl tracking-[-0.03em] text-slate-950">Environment</CardTitle>
                </CardHeader>
                <CardContent className="p-8 pt-2">
                    <div className="flex items-center gap-4">
                        <Button 
                            variant={theme === 'light' ? 'default' : 'outline'} 
                            onClick={() => setTheme('light')}
                            className={`h-12 gap-2 rounded-xl px-6 ${theme === 'light' ? 'bg-slate-900 text-white hover:bg-slate-800' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
                        >
                            <Sun size={18} /> Light
                        </Button>
                         <Button 
                            variant={theme === 'dark' ? 'default' : 'outline'} 
                            onClick={() => setTheme('dark')}
                             className={`h-12 gap-2 rounded-xl px-6 ${theme === 'dark' ? 'bg-slate-900 text-white hover:bg-slate-800' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
                        >
                            <Moon size={18} /> Dark
                        </Button>
                    </div>
                </CardContent>
            </Card>
            
            {/* Danger Zone */}
             <Card className="rounded-3xl border-red-200 bg-red-50/30 shadow-sm">
                <CardHeader className="p-8 pb-4">
                    <CardTitle className="text-2xl tracking-[-0.03em] text-red-900">Data Erasure</CardTitle>
                </CardHeader>
                <CardContent className="p-8 pt-2">
                    <p className="text-sm text-red-700 mb-6 max-w-md">
                        Permanently delete your account, quotes, and settings. This action cannot be undone.
                    </p>
                    <Button variant="destructive" onClick={initiateAccountDeletion} disabled={isDeletingAccount} className="rounded-xl bg-red-600 text-white hover:bg-red-700">
                        {isDeletingAccount ? <Loader2 className="animate-spin mr-2"/> : <Trash2 size={18} className="mr-2"/>}
                        Delete Account
                    </Button>
                </CardContent>
            </Card>

            <ReauthDialog 
                isOpen={showReauthDialog} 
                onOpenChange={setShowReauthDialog} 
                onConfirm={async (pw) => {
                    try {
                        if (!currentUser?.email) return;
                        const cred = EmailAuthProvider.credential(currentUser.email, pw);
                        await reauthenticateWithCredential(currentUser, cred);
                        setShowReauthDialog(false);
                        performDeleteAccount();
                    } catch (error: any) {
                        sonnerToast.error("Incorrect password. Please try again.");
                    }
                }}
                isProcessing={isDeletingAccount}
            />
        </div>
    );
};

export default AccountSettings;
